/**
 * Register the service worker and handle update notifications.
 * Call this once from a client component (e.g. layout).
 *
 * In dev mode (NODE_ENV !== 'production'), the SW is actively UNREGISTERED
 * and all caches are cleared on every load. The SW does cache-first on `.js`
 * which makes HMR useless: edited hooks/components keep loading the previous
 * bundle from cache. Production behavior is unchanged.
 */
export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  if (process.env.NODE_ENV !== "production") {
    window.addEventListener("load", async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch (err) {
        console.warn("Dev SW unregister failed:", err);
      }
    });
    return;
  }

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });

      // Check for updates periodically (every 60 minutes)
      setInterval(
        () => {
          registration.update();
        },
        60 * 60 * 1000,
      );

      // Handle updates — notify the user when a new version is available
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            // A new version is ready — dispatch a custom event so the UI can show
            // a toast or banner prompting the user to refresh.
            window.dispatchEvent(
              new CustomEvent("closerai:sw-update", {
                detail: { registration },
              }),
            );
          }
        });
      });
    } catch (err) {
      console.warn("Service worker registration failed:", err);
    }
  });
}
